import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import { api, inr } from "@/lib/api";

const FEATURE_LABELS = {
  sales_invoice: "Sales Invoices",
  quotation: "Quotations",
  customers: "Customers",
  items: "Items & Catalog",
  dashboard: "Business Dashboard",
  pdf_invoice: "PDF Invoices",
  purchase_invoice: "Purchase Invoices",
  inventory_full: "Full Inventory & Stock",
  expenses: "Expenses Module",
  gst_billing: "GST Billing & Reports",
  reports_advanced: "Advanced Reports",
  whatsapp: "WhatsApp Sharing",
  multi_user: "Multi-User Access",
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/plans/public").then((r) => setPlans(r.data));
  }, []);

  return (
    <PublicLayout>
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-3">Simple pricing</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Pay only ₹99/month</h1>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">Start with a 14-day free trial. Upgrade when you&apos;re ready. Cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p) => (
            <Card key={p.id} className={`p-6 flex flex-col ${p.featured ? "border-primary shadow-lg relative ring-2 ring-primary/10" : ""}`} data-testid={`plan-${p.code}`}>
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="text-xs">Most Popular</Badge>
                </div>
              )}
              <div className="font-heading font-bold text-xl">{p.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{p.description}</div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-heading font-bold text-4xl">{p.price_inr === 0 ? "Free" : inr(p.price_inr)}</span>
                {p.price_inr > 0 && <span className="text-sm text-muted-foreground">/month</span>}
              </div>
              {p.trial_days > 0 && p.price_inr === 0 && (
                <div className="mt-2 text-xs text-muted-foreground">{p.trial_days}-day free trial</div>
              )}
              <Button asChild size="lg" className="mt-6" variant={p.featured ? "default" : "outline"} data-testid={`plan-cta-${p.code}`}>
                <Link to="/signup">{p.price_inr === 0 ? "Start Free Trial" : "Choose Plan"}</Link>
              </Button>
              <ul className="mt-6 space-y-2 text-sm">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                  const enabled = p.features?.[key];
                  return (
                    <li key={key} className={`flex items-center gap-2 ${enabled ? "" : "text-muted-foreground/60"}`}>
                      {enabled ? <Check size={14} className="text-primary flex-shrink-0" /> : <X size={14} className="text-muted-foreground/50 flex-shrink-0" />}
                      <span className={enabled ? "" : "line-through"}>{label}</span>
                    </li>
                  );
                })}
              </ul>
              {p.limits && (
                <div className="mt-4 pt-4 border-t text-xs text-muted-foreground space-y-1">
                  <div>Invoices: {p.limits.invoices === -1 ? "Unlimited" : p.limits.invoices}</div>
                  <div>Customers: {p.limits.customers === -1 ? "Unlimited" : p.limits.customers}</div>
                  <div>Users: {p.limits.users}</div>
                </div>
              )}
            </Card>
          ))}
        </div>

        <div className="mt-16 max-w-2xl mx-auto text-center">
          <div className="font-heading font-semibold">Frequently asked</div>
          <div className="mt-4 grid gap-3 text-left text-sm">
            <FAQ q="Is there a really a free trial?" a="Yes — 14 days, no credit card required. Full access to invoicing, quotations, customers and PDF exports." />
            <FAQ q="Can I cancel any time?" a="Yes. You can cancel your subscription from the Subscription page. You keep access until the end of the paid month." />
            <FAQ q="Will my data be safe if I don't renew?" a="Absolutely. Your business data is never auto-deleted. Just renew to regain full access." />
            <FAQ q="Which payment methods work?" a="UPI, Cards, NetBanking and Wallets via Razorpay. Payments are securely verified on our servers." />
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function FAQ({ q, a }) {
  return (
    <details className="p-4 rounded-lg border bg-card group">
      <summary className="font-medium cursor-pointer flex items-center justify-between">
        {q}
        <span className="text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="mt-2 text-muted-foreground text-sm">{a}</div>
    </details>
  );
}
